import { useMarqueeSettings } from '@/hooks/useMarqueeSettings';
import { useLanguage } from '@/contexts/LanguageContext';
import almoosa from '@/assets/partners/almoosa.png';
import alfateh from '@/assets/partners/alfateh.png';
import saudiJournalists from '@/assets/partners/saudi-journalists.png';
import takamul from '@/assets/partners/takamul.png';
import sat from '@/assets/partners/sat.png';
import saudiFriendship from '@/assets/partners/saudi-friendship.png';
import rayaheen from '@/assets/partners/rayaheen.png';
import quran from '@/assets/partners/quran.png';
import alwaha from '@/assets/partners/alwaha.png';
import cognia from '@/assets/partners/cognia.png';
import etec from '@/assets/partners/etec.png';
import mawhiba from '@/assets/partners/mawhiba.png';

const partners = [
  { src: almoosa, alt: 'الموسى الصحية' },
  { src: alfateh, alt: 'نادي الفتح' },
  { src: saudiJournalists, alt: 'هيئة الصحفيين السعوديين' },
  { src: takamul, alt: 'مركز تكامل' },
  { src: sat, alt: 'Study Abroad' },
  { src: saudiFriendship, alt: 'جمعية أصدقاء السعودية للسياحة' },
  { src: rayaheen, alt: 'جمعية الرياحين' },
  { src: quran, alt: 'جمعية تحفيظ القرآن بالأحساء' },
  { src: alwaha, alt: 'الواحة نيوز' },
];

const accreditations = [
  { src: cognia, alt: 'Cognia Accredited' },
  { src: etec, alt: 'هيئة تقويم التعليم والتدريب' },
  { src: mawhiba, alt: 'موهبة' },
];

interface StripProps {
  items: { src: string; alt: string }[];
  speed: number;
  fadeSize: number;
  logoHeight: string;
  reverse?: boolean;
}

const MarqueeStrip = ({ items, speed, fadeSize, logoHeight, reverse }: StripProps) => {
  // Duplicate enough to ensure overflow for short lists
  const repeats = items.length < 6 ? 4 : 2;
  const loop = Array.from({ length: repeats }, () => items).flat();
  const fadeMask = `linear-gradient(to right, transparent 0, #000 ${fadeSize}px, #000 calc(100% - ${fadeSize}px), transparent 100%)`;
  const animName = reverse ? 'marquee-x-reverse' : 'marquee-x';

  return (
    <div
      className="w-full overflow-hidden"
      style={{ WebkitMaskImage: fadeMask, maskImage: fadeMask }}
      dir="ltr"
    >
      <div
        className="flex items-center gap-10 md:gap-14 w-max will-change-transform motion-reduce:animate-none"
        style={{ animation: `${animName} ${speed}s linear infinite` }}
      >
        {loop.map((l, i) => (
          <img
            key={i}
            src={l.src}
            alt={l.alt}
            loading="lazy"
            className={`${logoHeight} w-auto object-contain opacity-[0.82] hover:opacity-100 transition-opacity duration-500 grayscale-[10%]`}
          />
        ))}
      </div>
    </div>
  );
};

const PartnersMarquee = () => {
  const { settings } = useMarqueeSettings();
  const { t } = useLanguage();

  if (!settings.enabled) return null;

  const partnersSpeed = settings.desktopSpeed;
  const accredSpeed = Math.round(settings.desktopSpeed * 1.4);
  const fadeSize = settings.fadeSize;

  return (
    <section
      aria-label={t('شركاء واعتمادات أكاديمية الكفاح', 'Kifah Academy Partners & Accreditations')}
      className="relative w-full pt-7 md:pt-10 pb-10 md:pb-14"
    >
      {/* PARTNERS */}
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-center mb-5 md:mb-7">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-foreground/15" />
            <span className="text-[10px] md:text-[11px] tracking-[0.2em] font-semibold text-foreground/55 uppercase">
              {t('شركاؤنا', 'Partners')}
            </span>
            <span className="h-px w-10 bg-foreground/15" />
          </div>
        </div>
        <MarqueeStrip
          items={partners}
          speed={partnersSpeed}
          fadeSize={fadeSize}
          logoHeight="h-10 md:h-12"
        />
      </div>

      {/* ACCREDITATIONS */}
      <div>
        <div className="flex items-center justify-center mb-5 md:mb-7">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[hsl(40_55%_55%/0.35)]" />
            <span className="text-[10px] md:text-[11px] tracking-[0.22em] font-semibold text-[hsl(40_55%_45%)] uppercase">
              {t('الاعتمادات', 'Accreditations')}
            </span>
            <span className="h-px w-10 bg-[hsl(40_55%_55%/0.35)]" />
          </div>
        </div>
        <MarqueeStrip
          items={accreditations}
          speed={accredSpeed}
          fadeSize={fadeSize}
          logoHeight="h-12 md:h-14"
          reverse
        />
      </div>

      <style>{`
        @keyframes marquee-x {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-x-reverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </section>
  );
};

export default PartnersMarquee;
